import React, { useState } from 'react';
import { Form, Button, Modal } from 'antd';
import { RcFile } from 'antd/lib/upload';

import {
  identity, isAdmin, isSuperAdmin, requiredValidation,
} from 'utils';
import {
  Text, Input,
} from 'components/components'
import approvedRejectedOptions from 'components/AngelInvestorsOverview/utils/constants'
import { SAFENoteDetailsDrawerProps } from 'types/startups/editStartup';
import { getStartupFile } from 'components/EditStartup/utils'

import './SAFENoteDetailsDrawer.scss';

const getStatusMessage = (approvedStatus: number, hasFile: boolean) => {
  if (!hasFile) return 'Not uploaded.';
  switch (approvedStatus) {
    case 0:
      return 'Waiting for review.';
    case 1:
      return 'Approved.';
    case 2:
      return 'Rejected.';
    default:
      return 'Not uploaded.';
  }
};

const SAFENoteDetailsDrawer = ({
  info,
  userType,
  actions,
}: SAFENoteDetailsDrawerProps) => {
  const [form] = Form.useForm();
  const statusFieldValue = Form.useWatch('status', form);
  const [file, setFile] = useState<Blob | string>('');
  const { id } = info;
  const isAdminOrSuperAdmin = isAdmin(userType) || isSuperAdmin(userType);
  const hasFile = identity.isTruthyString(info.safeNote);

  const {
    updateStartupById,
    setIsDrawerOpen,
  } = actions;

  const status = getStatusMessage(info.safeNoteApprovedStatus ?? 0, hasFile);

  const handleApproveReject = ({
    status: newStatus,
    reason,
  }: {
    status: number,
    reason: string
  }) => {
    if (newStatus === 1) {
      Modal.confirm({
        title: 'Confirm Approval',
        content: 'Are you sure you want to approve this SAFE Note? This will top up the startup\'s wallet by 500,000 AED and update their status to Community.',
        okText: 'Confirm',
        cancelText: 'Cancel',
        onOk: () => {
          updateStartupById({
            id,
            startup: {
              safeNoteApprovedStatus: newStatus,
              safeNoteRejectReason: '',
            },
          });
          setIsDrawerOpen(false);
        },
      });
    } else {
      updateStartupById({
        id,
        startup: {
          safeNoteApprovedStatus: newStatus,
          safeNoteRejectReason: reason,
        },
      });
      setIsDrawerOpen(false);
    }
  }

  const handleUpload = () => {
    if (file) {
      updateStartupById({
        id,
        startup: {
          safeNote: file as string,
          safeNoteApprovedStatus: 0,
          safeNoteRejectReason: '',
        },
      });
      setIsDrawerOpen(false);
    }
  }

  const beforeUpload = (uploadFile: RcFile) => {
    setFile(uploadFile as Blob);
    return false;
  }

  const handleCancel = () => {
    form.resetFields();
    setIsDrawerOpen(false);
  }

  const options = [
    ...approvedRejectedOptions.map((
      { title, value },
    ) => ({ label: title, value })),
  ]

  return (
    <div className="safe-note-details-drawer">
      <Text text={`Status: ${status}`} weight={400} />

      {hasFile && (
        <Input.DraggerUpload
          previewType="image"
          defaultFileList={[...getStartupFile(info.safeNote!)]}
          isRoundableButton
        />
      )}

      {!isAdminOrSuperAdmin && info.safeNoteApprovedStatus === 2
        && identity.isTruthyString(info.safeNoteRejectReason) && (
        <Text
          text={`Rejection Reason: ${info.safeNoteRejectReason}`}
          weight={400}
        />
      )}

      {isAdminOrSuperAdmin && hasFile ? (
        <Form
          form={form}
          onFinish={handleApproveReject}
          initialValues={{
            status: info.safeNoteApprovedStatus || undefined,
            reason: info.safeNoteRejectReason || '',
          }}
          validateTrigger="onChange"
        >
          <Input
            name="status"
            label="Status"
            layout="horizontal"
          >
            <Input.Select
              options={options}
              placeholder={status}
              allowClear
            />
          </Input>

          {statusFieldValue === 2 && (
            <Input
              name="reason"
              label="Rejection Reason"
              layout="vertical"
              required
              requiredMark
              rules={[requiredValidation]}
            >
              <Input.InputField.TextArea
                size="large"
                rows={4}
                placeholder="Enter Rejection Reason"
              />
            </Input>
          )}

          <div className="drawer-buttons" style={{ marginTop: 16 }}>
            <Button
              type="default"
              className="secondary-button"
              onClick={handleCancel}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              className="primary-button"
              onClick={form.submit}
            >
              Save
            </Button>
          </div>
        </Form>
      ) : (
        (!hasFile || (!isAdminOrSuperAdmin && info.safeNoteApprovedStatus === 2)) && (
          <div>
            <p className="message">
              {info.safeNoteApprovedStatus === 2
                ? 'Please upload a new SAFE Note for review'
                : 'Please upload signed SAFE Note'}
            </p>
            <Input.DraggerUpload
              accept=".pdf,.doc,.docx"
              beforeUpload={beforeUpload}
              previewType="image"
              isRoundableButton
            />
            <div className="drawer-buttons" style={{ marginTop: 16 }}>
              <Button
                type="default"
                className="secondary-button"
                onClick={handleCancel}
                style={{ marginRight: 8 }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                className="primary-button"
                onClick={handleUpload}
                disabled={!file}
              >
                Upload
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default SAFENoteDetailsDrawer;
